<template>
  <div class="virtual-court-container">
    <el-card>
      <!-- 顶部操作区域 -->
      <div class="header">
        <el-button type="primary" @click="openAddDialog">新增线上执法室</el-button>
        <el-input 
          v-model="searchName" 
          placeholder="请输入线上执法室名称" 
          style="width: 200px; margin-left: 16px;" 
          @keyup.enter.native="handleSearch"
          @input="handleSearchInput"
          clearable
        />
        <el-button type="text" @click="handleSearch" style="margin-left: 8px;">搜索</el-button>
        <el-button type="text" @click="resetSearch">重置</el-button>
      </div>

      <!-- 虚拟仲裁庭表格 -->
      <el-table 
        :data="courtList" 
        style="width: 100%; margin-top: 16px;"
        v-loading="loading"
        element-loading-text="加载中..."
      >
        <el-table-column prop="name" label="线上执法室名称" min-width="200" />
        <el-table-column prop="isValid" label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.isValid === 1 ? 'success' : 'danger'">
              {{ scope.row.isValid === 1 ? '有效' : '无效' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="180" align="center">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" align="center">
          <template slot-scope="scope">
            <el-button 
              size="mini" 
              type="text" 
              icon="el-icon-edit" 
              @click="openEditDialog(scope.row)"
            >编辑</el-button>
            <el-button 
              size="mini" 
              type="text" 
              icon="el-icon-delete" 
              class="delete-btn"
              @click="handleDelete(scope.row)"
            >删除</el-button>
          </template>
        </el-table-column>
    </el-table>

    <!-- 分页组件 -->
      <div class="pagination-container">
    <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pageNum"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
      background
        />
      </div>
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="480px"
      @close="resetForm"
      :close-on-click-modal="false"
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-width="140px">
        <el-form-item label="线上执法室名称" prop="name" required>
          <el-input 
            v-model="form.name" 
            placeholder="请输入线上执法室名称"
            maxlength="20"
            show-word-limit
            clearable
            @input="handleInputChange"
            class="court-name-input"
          />
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false" size="medium">取消</el-button>
        <el-button type="primary" @click="handleSubmit" size="medium" :loading="submitLoading">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'

export default {
  name: 'VirtualCourt',
  data() {
    return {
      // 数据定义
      courtList: [],
      total: 0,
      pageNum: 1,
      pageSize: 10,
      loading: false,
      searchName: '',
      searchTimer: null, // 搜索防抖定时器

      // 弹窗相关
      dialogVisible: false,
      dialogTitle: '',
      submitLoading: false,
      form: {
        courtId: '',
        name: '',
        isValid: 1
      },

      // 表单验证规则
      rules: {
        name: [
          { 
            required: true, 
            message: '请输入线上执法室名称', 
            trigger: ['blur', 'change'] // 添加change触发器确保验证正确触发
          },
          { 
            min: 2, 
            max: 50, 
            message: '长度在 2 到 50 个字符', 
            trigger: ['blur', 'change'] // 添加change触发器
          },
          { 
            validator: (rule, value, callback) => {
              if (!value) {
                callback();
                return;
              }
              
              // 不能只包含空格
              if (value.trim().length === 0) {
                callback(new Error('名称不能只包含空格'));
                return;
              }
              
              // 不能包含特殊字符（只允许中文、英文、数字、空格、括号、横线）
              const pattern = /^[\u4e00-\u9fa5a-zA-Z0-9\s\(\)\-]+$/;
              if (!pattern.test(value)) {
                callback(new Error('名称只能包含中文、英文、数字、空格、括号、横线'));
                return;
              }
              
              // 不能以空格开头或结尾
              if (value.startsWith(' ') || value.endsWith(' ')) {
                callback(new Error('名称不能以空格开头或结尾'));
                return;
              }
              
              // 不能连续多个空格
              if (/\s{2,}/.test(value)) {
                callback(new Error('名称不能包含连续的空格'));
                return;
              }
              
              callback();
            }, 
            trigger: ['blur', 'change'] // 添加change触发器
          }
        ]
      }
    }
  },
  mounted() {
    this.fetchCourtList()
  },
  methods: {
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    // 分页查询虚拟仲裁庭
    async fetchCourtList() {
      this.loading = true
      try {
        const res = await request.get('/api/virtualCourt/page', {
          params: {
            pageNum: this.pageNum,
            pageSize: this.pageSize,
            name: this.searchName !== '' ? this.searchName : undefined // 修复：空字符串也传递，只有undefined才不传递
          }
        })
        
        if (res.code === 200) {
          this.courtList = res.data.records || []
          this.total = res.data.total || 0
        } else {
          console.error('获取虚拟仲裁庭列表失败:', res.message)
        }
      } catch (error) {
        console.error('请求失败:', error)
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.pageNum = 1
      this.fetchCourtList()
    },

    // 处理搜索输入，支持空格搜索
    handleSearchInput() {
      // 实时搜索，延迟500ms执行，避免频繁请求
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.handleSearch()
      }, 500)
    },

    // 重置搜索
    resetSearch() {
      this.searchName = ''
      this.pageNum = 1
      this.fetchCourtList()
    },

    // 分页大小改变
    handleSizeChange(val) {
      this.pageSize = val
      this.pageNum = 1
      this.fetchCourtList()
    },

    // 当前页改变
    handleCurrentChange(val) {
      this.pageNum = val
      this.fetchCourtList()
    },

    // 打开新增弹窗
    openAddDialog() {
      this.dialogTitle = '新增线上执法室'
      this.dialogVisible = true
      this.form = {
        courtId: '',
        name: '',
        isValid: 1
      }
    },

    // 打开编辑弹窗
    openEditDialog(row) {
      this.dialogTitle = '编辑线上执法室'
      this.dialogVisible = true
      this.form = {
        courtId: row.courtId,
        name: row.name,
        isValid: row.isValid
      }
    },

    // 重置表单
    resetForm() {
      this.form = {
        courtId: '',
        name: '',
        isValid: 1
      }
      // 清除验证错误信息
      this.$nextTick(() => {
        if (this.$refs.formRef) {
          this.$refs.formRef.clearValidate()
        }
      })
    },

    // 处理输入变化，实时验证
    handleInputChange() {
      // 实时验证输入内容
      this.$nextTick(() => {
        if (this.$refs.formRef) {
          this.$refs.formRef.validateField('name')
        }
      })
    },

    // 提交表单
    async handleSubmit() {
      try {
        // 先进行前端表单验证
        const valid = await this.$refs.formRef.validate()
        if (!valid) {
          return // 前端验证失败，不提交
        }
        
        this.submitLoading = true
        
        if (this.form.courtId) {
          // 编辑模式 - 调用更新接口
          const res = await request.put('/api/virtualCourt/update', this.form)
          this.$message.success('更新成功')
          this.dialogVisible = false
          this.fetchCourtList()
        } else {
          // 新增模式 - 调用新增接口，传递名称和租户ID
          const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
          const res = await request.post('/api/virtualCourt/add', {
            name: this.form.name,
            tenantId: userInfo.tenantId // 从localStorage获取租户ID
          })
          this.$message.success('新增成功')
          this.dialogVisible = false
          this.fetchCourtList()
        }
      } catch (error) {
        console.error('提交失败:', error)
      } finally {
        this.submitLoading = false
      }
    },

    // 删除线上执法室
    async handleDelete(row) {
      try {
        await this.$confirm('确定要删除这个线上执法室吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const res = await request.delete(`/api/virtualCourt/delete/${row.courtId}`)
        this.$message.success('删除成功')
        this.fetchCourtList()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          // 不显示错误弹窗，静默处理
        }
      }
    }
  }
}
</script>

<style scoped>
.virtual-court-container {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: calc(100vh - 50px);
}

.header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.delete-btn {
  color: #f56c6c;
}

.delete-btn:hover {
  color: #f78989;
}

.dialog-footer {
  text-align: right;
}

/* 解决输入框文本与清除按钮、字符计数重叠问题 */
.court-name-input >>> .el-input__inner {
  padding-right: 85px !important;
}

.court-name-input >>> .el-input__suffix {
  right: 10px;
}
</style> 